/**
 * Step 12 (renumbered, was 15) — Daily Nudge toggle + Push permission
 *
 * Spec: specs/onboarding-rebuild.md → Act 3 step 12 (combined 2026-05-16)
 *
 * Mason 2026-05-18: killed the email-reminder path entirely (MailerLite
 * function + cron + group + custom fields removed). The picker collapses
 * to a single yes/no toggle: "Want a daily nudge?" — Yes maps to
 * reminderMethod='notifications', No maps to reminderMethod='none'. Copy
 * adapts to the device the user is on (browser pop-up vs. phone-screen
 * tap vs. needs-PWA-install on iPhone Safari).
 *
 * Mason 2026-05-16 (prior): combined old steps 12 + 13 into one screen.
 * Single "Notifications" option that adapts its label + behavior per device:
 *   - desktop browser → "Browser notifications"
 *   - Android / iPhone PWA → "Phone push"
 *   - iPhone Safari (no PWA) → "Phone push (needs home-screen install)"
 *
 * When Yes picked, a sectioned card slides in below with EITHER:
 *   - 5 notification toggles + "Allow these" button (for working devices), OR
 *   - 3-step Add-to-Home-Screen guide (for iPhone Safari no-PWA)
 *
 * Browser-notification option dropped — it's the same Web Push API as
 * phone push, just displayed on a different surface. Collapsing them
 * into one device-aware label removes the user confusion.
 *
 * timeGoal/onTimeChange/timeOptions props still accepted for backward
 * compat with OnboardingPreview wiring — minutes-per-day moved to step 10
 * profile-data bundle on the prior restructure.
 */

import { useMemo, useState } from 'react'
import { getPushSupportLevel, isMobile as detectIsMobile } from '@/lib/deviceCapabilities'
import { NOTIFICATION_TOGGLES } from '@/components/onboarding/steps/16-push-permission/index.jsx'
import ContinueButton from '@/components/onboarding/ContinueButton'

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'

// Device-aware blurb for the "Yes — buzz me" option. Three states:
//   - desktop browser → pop-up
//   - mobile (Android or iOS-as-PWA) → tap on phone screen
//   - iPhone Safari (no PWA) → tap + the install-required hint
function resolveYesBlurb(level, mobile) {
  if (level === 'needs_pwa_install') {
    return 'A quick tap on your phone screen, once a day — needs Illuminate added to your home screen first.'
  }
  if (mobile) return 'A quick tap on your phone screen, once a day.'
  return 'A quick pop-up in your browser, once a day.'
}

/* ---------------------------- YesNoRow ---------------------------------- */
// Replaces the legacy OptionRow that rendered 3-method picker (none/email/
// notifications). The picker is now a binary "Yes — buzz me" / "No, I'll
// come back on my own", so the row visual leans richer: icon + title + blurb
// instead of a single label line.
function YesNoRow({ selected, onSelect, icon, title, blurb }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className="w-full text-left rounded-xl focus:outline-none"
      style={{
        background: selected ? 'var(--gold-select)' : 'var(--bg-surface-high)',
        border: selected ? '1.5px solid var(--gold)' : '1px solid var(--glass-border)',
        padding: selected ? '15.5px 17.5px' : '16px 18px',
        transition: `background 240ms ${EASE}, border-color 240ms ${EASE}`,
      }}
    >
      <div className="flex items-start gap-3">
        <span aria-hidden className="text-[20px] leading-none flex-shrink-0 mt-0.5">
          {icon}
        </span>
        <span className="flex-1">
          <span
            className="block text-[15px] font-semibold leading-tight"
            style={{ color: 'var(--text-primary)' }}
          >
            {title}
          </span>
          <span
            className="block text-[12.5px] mt-1"
            style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}
          >
            {blurb}
          </span>
        </span>
        <span
          aria-hidden="true"
          className="flex-shrink-0 flex items-center justify-center mt-1"
          style={{
            width: '18px',
            height: '18px',
            opacity: selected ? 1 : 0,
            transform: selected ? 'scale(1)' : 'scale(0.6)',
            transition: `opacity 240ms ${EASE}, transform 240ms ${EASE}`,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <circle cx="9" cy="9" r="9" fill="var(--gold)" />
            <path
              d="M5 9.2 7.6 11.7 13 6.3"
              stroke="var(--text-on-gold)"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>
    </button>
  )
}

/* ------------------------ NotificationToggle ---------------------------- */
function NotificationToggle({ toggle, on, onToggle }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(toggle.key)}
      role="switch"
      aria-checked={on}
      className="w-full flex items-start gap-3 px-3.5 py-3 rounded-lg text-left"
      style={{
        background: on ? 'var(--gold-select)' : 'var(--bg-surface-high)',
        border: `1px solid ${on ? 'var(--gold-dim)' : 'var(--glass-border)'}`,
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-body)',
        transition: `background 180ms ${EASE}, border-color 180ms ${EASE}`,
        cursor: 'pointer',
      }}
    >
      <span aria-hidden className="text-[18px] leading-none flex-shrink-0 mt-0.5">
        {toggle.icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[14px] font-semibold leading-tight">{toggle.label}</span>
        <span className="block text-[12px] text-[var(--text-muted)] mt-0.5">{toggle.desc}</span>
      </span>
      {/* Tiny pill switch */}
      <span
        aria-hidden
        className="relative shrink-0 inline-block"
        style={{
          width: 32,
          height: 18,
          borderRadius: 999,
          background: on ? 'var(--gold)' : 'var(--bg-surface)',
          border: `1px solid ${on ? 'var(--gold)' : 'var(--glass-border)'}`,
          transition: `background 180ms ${EASE}, border-color 180ms ${EASE}`,
          marginTop: 2,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 1,
            left: on ? 15 : 1,
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: on ? 'var(--text-on-gold)' : 'var(--text-muted-soft)',
            transition: `left 180ms ${EASE}, background 180ms ${EASE}`,
          }}
        />
      </span>
    </button>
  )
}

/* --------------------- iOS Add-to-Home-Screen guide --------------------- */
function IOSInstallGuide() {
  return (
    <div
      className="space-y-3"
      style={{
        animation: `rpFadeIn 400ms ${EASE}`,
      }}
    >
      <p className="text-[13px] text-[var(--text-muted)] leading-relaxed">
        Apple only lets web apps send push to your phone if Illuminate lives on your home screen.
        Three taps and you’re done:
      </p>
      <ol className="space-y-2.5">
        <GuideStep
          n="1"
          text={
            <>
              Tap the <strong>Share</strong> icon at the bottom of Safari (
              <span aria-hidden>↑</span> arrow pointing up out of a square).
            </>
          }
        />
        <GuideStep
          n="2"
          text={
            <>
              Scroll down and tap <strong>Add to Home Screen</strong>.
            </>
          }
        />
        <GuideStep
          n="3"
          text={
            <>
              Open Illuminate from your new home-screen icon. Push will work then — come back here
              and pick Notifications again.
            </>
          }
        />
      </ol>
      <p className="text-[11px] text-[var(--text-muted-soft)] italic mt-3">
        Or pick "No, I'll come back on my own" for now — you can always flip this on later from
        Account.
      </p>
    </div>
  )
}

function GuideStep({ n, text }) {
  return (
    <li className="flex items-start gap-3">
      <span
        aria-hidden
        className="flex-shrink-0 inline-flex items-center justify-center font-heading font-bold text-[12px]"
        style={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: 'var(--gold)',
          color: 'var(--text-on-gold)',
          marginTop: 1,
        }}
      >
        {n}
      </span>
      <span className="flex-1 text-[13px] text-[var(--text-primary)] leading-snug">{text}</span>
    </li>
  )
}

/* ============================== Main =================================== */
export default function ReminderPickerSynthesis({ reminderMethod, onMethodChange, onContinue }) {
  const [notifPrefs, setNotifPrefs] = useState(() =>
    Object.fromEntries(NOTIFICATION_TOGGLES.map(t => [t.key, t.defaultOn]))
  )
  const [allowed, setAllowed] = useState(false)

  // Device support level is computed at mount (window/navigator-bound).
  // useMemo to avoid recomputing every render — doesn't change without reload.
  const pushLevel = useMemo(() => getPushSupportLevel(), [])
  const mobile = useMemo(() => detectIsMobile(), [])
  const yesBlurb = useMemo(() => resolveYesBlurb(pushLevel, mobile), [pushLevel, mobile])

  // Toggle semantics:
  //   reminderMethod === 'notifications' → user picked Yes
  //   reminderMethod === 'none'           → user picked No
  //   otherwise (null/undefined/'email')  → unset (legacy 'email' value is
  //     coerced to unset so existing-email users land on a clean picker)
  const wantNudge =
    reminderMethod === 'notifications' ? true : reminderMethod === 'none' ? false : null

  // Notifications-config section: visible whenever Yes is picked.
  const showNotificationsSection = wantNudge === true
  const needsIOSInstall = pushLevel === 'needs_pwa_install'
  const anyToggleOn = Object.values(notifPrefs).some(Boolean)

  // Continue gating:
  //   - No (none): enabled as soon as picked
  //   - Yes + needs-PWA-install: enabled (user advances; they'll install +
  //     come back through /account to re-pick once installed)
  //   - Yes + works: enabled when user has hit Allow OR at least one toggle
  //     is on (accept toggle-config as commitment even without OS prompt)
  const canContinue =
    wantNudge === false || (showNotificationsSection && (needsIOSInstall || allowed || anyToggleOn))

  function handleToggle(key) {
    setNotifPrefs(p => ({ ...p, [key]: !p[key] }))
  }

  function handleAllow() {
    setAllowed(true)
    // Production wrapper wires this to usePushNotifications.requestPermission()
    // — preview just marks the state so Continue activates.
  }

  return (
    <div className="w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <style>{`
        @keyframes rpFadeIn {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Heading */}
      <div className="text-center mb-8 sm:mb-10">
        <h1
          className="font-heading text-[26px] sm:text-[32px] font-extrabold text-[var(--text-primary)]"
          style={{ lineHeight: 1.15, letterSpacing: '-0.012em' }}
        >
          Want a daily nudge?
        </h1>
        <p
          className="text-[var(--text-muted)] text-[13px] sm:text-sm mt-3 mx-auto max-w-[36ch]"
          style={{ lineHeight: 1.55 }}
        >
          Five seconds, once a day — your call. Change this anytime in settings.
        </p>
      </div>

      {/* ───────── Section 1: Yes/No toggle ───────── */}
      <section
        className="rounded-2xl p-5 sm:p-6 mb-5"
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--glass-border)',
        }}
      >
        <div role="radiogroup" aria-label="Want a daily nudge?" className="flex flex-col gap-2">
          <YesNoRow
            selected={wantNudge === true}
            onSelect={() => onMethodChange('notifications')}
            icon="🔔"
            title="Yes — buzz me"
            blurb={yesBlurb}
          />
          <YesNoRow
            selected={wantNudge === false}
            onSelect={() => onMethodChange('none')}
            icon="✋"
            title="No, I'll come back on my own"
            blurb="No notifications. You'll come back when you want."
          />
        </div>
      </section>

      {/* ───────── Section 2: Notifications config (conditional) ───────── */}
      {showNotificationsSection && (
        <section
          key={pushLevel}
          className="rounded-2xl p-5 sm:p-6 mb-5"
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--gold-dim)',
            animation: `rpFadeIn 400ms ${EASE}`,
          }}
        >
          {needsIOSInstall ? (
            <>
              <h2 className="font-heading text-[17px] text-[var(--text-primary)] mb-3">
                Quick step on iPhone Safari.
              </h2>
              <IOSInstallGuide />
            </>
          ) : (
            <>
              <h2 className="font-heading text-[17px] text-[var(--text-primary)] mb-1">
                What kind of notifications?
              </h2>
              <p className="text-[12px] text-[var(--text-muted)] mb-4">
                Pick what shows up. Skip anything you don’t want.
              </p>
              <div className="space-y-2">
                {NOTIFICATION_TOGGLES.map(t => (
                  <NotificationToggle
                    key={t.key}
                    toggle={t}
                    on={!!notifPrefs[t.key]}
                    onToggle={handleToggle}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={handleAllow}
                disabled={allowed || !anyToggleOn}
                className="mt-5 w-full rounded-xl py-2.5 px-4 text-sm font-semibold"
                style={{
                  background: allowed ? 'var(--bg-surface-high)' : 'var(--gold)',
                  color: allowed ? 'var(--text-muted)' : 'var(--text-on-gold)',
                  border: `1px solid ${allowed ? 'var(--glass-border)' : 'var(--gold)'}`,
                  cursor: allowed || !anyToggleOn ? 'not-allowed' : 'pointer',
                  opacity: !anyToggleOn ? 0.5 : 1,
                  transition: `background 180ms ${EASE}, color 180ms ${EASE}`,
                  fontFamily: 'var(--font-body)',
                }}
              >
                {allowed ? '✓ Allowed' : 'Allow these →'}
              </button>
            </>
          )}
        </section>
      )}

      {/* Continue — wakes up after method picked (+ notifications addressed) */}
      <div className="mt-7 sm:mt-9 flex justify-center">
        <ContinueButton onClick={onContinue} disabled={!canContinue}>
          Continue
        </ContinueButton>
        {/* Disabled-CTA hint retired 2026-05-17 (Mason lock) — the disabled
            Continue button + visible method picker are self-explanatory.
            Previous text: "Pick how we should remind you to continue." /
            "Pick at least one notification, then tap Allow." */}
      </div>
    </div>
  )
}
